import {
  ComponentProps,
  FC,
  ForwardedRef,
  forwardRef,
  ReactElement,
} from 'react';

import styled from '@emotion/styled';
import MuiDialog, { dialogClasses } from '@mui/material/Dialog';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';

import { IconButton } from '@web/ui/components';
import { CloseIcon } from '@web/ui/icons';

const Container = styled(MuiDialog)`
  .${dialogClasses.paper} {
    display: inline-grid;
    padding: ${({ theme }) => theme.spacing('2000')};
    gap: ${({ theme }) => theme.spacing('2000')};
    margin-inline: ${({ theme }) => theme.spacing('0500')};
    margin-block: ${({ theme }) => theme.spacing('2000')};
    border-radius: ${({ theme }) => theme.shape.corner['1250']};
    color: ${({ theme }) => theme.color.onSurface};
    background-color: ${({ theme }) => theme.color.surfaceContainer};
    box-shadow: ${({ theme }) => theme.elevation.level5};

    ${({ theme }) => theme.breakpoints.up('sm')} {
      padding: ${({ theme }) => theme.spacing('3000')};
      gap: ${({ theme }) => theme.spacing('3000')};
    }
  }

  .${dialogClasses.paperScrollBody} {
    max-width: 540px;
    box-sizing: border-box;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    .${dialogClasses.paperScrollBody} {
      max-width: calc(100% - 8px);
      margin-right: 0;
    }
  }
`;

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: ReactElement<any, any>;
  },
  ref: ForwardedRef<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CloseButton = styled(IconButton)`
  position: absolute;
  z-index: 5;
  top: calc(
    ${({ theme }) => theme.spacing('1000')} - 8px
      /* -8px to take into account mui padding */
  );
  right: calc(
    ${({ theme }) => theme.spacing('1000')} - 8px
      /* -8px to take into account mui padding */
  );
`;

export const ModalTitle = styled.span`
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['6']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.extended};
  text-transform: ${({ theme }) => theme.textTransform.capitalize};
`;

export const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing('2000')};
`;

export interface IModalProps
  extends Omit<ComponentProps<typeof Container>, 'onClose'> {
  className?: string;
  onClose(): void;
}

export const Modal: FC<IModalProps> = ({ children, onClose, ...props }) => (
  <Container
    role="modal"
    onClose={onClose}
    TransitionComponent={Transition}
    {...props}
  >
    <CloseButton onClick={onClose}>
      <CloseIcon />
    </CloseButton>

    {children}
  </Container>
);
